import { GoogleOAuthProvider } from "@react-oauth/google";
import { useContext, useMemo } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import ConfigProvider from "antd/es/config-provider";
import theme from "antd/es/theme";
import { Account } from "./components/Account";
import { Dashboard } from "./components/Dashboard";
import { Header } from "./components/Header";
import { JSONViewer } from "./components/JSONViewer";
import { Login } from "./components/Login";
import { Toast } from "./components/Toast";
import { AuthContext, AuthContextProvider } from "./context/AuthContext";
import { ToastContextProvider } from "./context/ToastContext";
import { PageEndpointEnum } from "./enums/endpoints";
import { Makers } from "./components/Makers";
import { makerEnvironment } from "./components/Makers/environment";
import { GlobalStyle } from "./style";
import { CLIENT_ID, isAffiliateEnvironment } from "./utils/env";
import { AffiliateAdminPage } from "./components/Affiliates/AffiliateAdminPage";
import { DataContextProvider } from "./context/DataContext";
import { Spinner } from "./components/shared/Spinner";
import { SpinnerContainerWrapper } from "./components/shared/Spinner/style";
import { Settings } from "./components/Settings";
import { Geolocations } from "./components/Geolocations";
import { Book } from "./components/Book";
import { TxViewer } from "./components/TxViewer";
import { RiskPage } from "./components/Risk";
import { MMTrades } from "./components/MMTrades";
import { OptionsTrades } from "./components/OptionsTrades";
import { MMStrategies } from "./components/MMStrategies";
import useScreenSize from "./hooks/api/screenSize/useScreenSize";
import { MobileHeader } from "./components/Header/MobileHeader";
import { MobileDashboard } from "./components/Dashboard/MobileDashboard";
import { MobileAccount } from "./components/Account/MobileAccount";
import { MobileTxViewer } from "./components/TxViewer/MobileTxViewer";
import { Debug } from "./components/Debug";
import { Withdraw } from "./components/Withdrawals";
import { Volume } from "./components/Volume";
import {
  BACKGROUND_COLORS,
  BORDER_COLORS,
  COLORS,
  LAYER_COLORS,
  TEXT_COLORS,
} from "./constants/design/colors";
import { Intercom } from "./components/Intercom";
import { Otc } from "./components/Otc";
import { Security } from "./components/Security";
import { Stake } from "./components/Stake";
import { LegacyStakeRedirect } from "./components/Stake/LegacyStakeRedirect";
import { MMMonitoring } from "./components/MMMonitoring";
import { MMControlPanel } from "./components/MMControlPanel";
import { TradingParameters } from "./components/TradingParameters";
import { LiveTrades } from "./components/LiveTrades";
import { PnL } from "./components/PnL";
import { PerpData } from "./components/PerpData";
import { Equity } from "./components/Equity";
import { IsolatedRiskMonitor } from "./components/IsolatedRiskMonitor";
import { Ondo } from "./components/Ondo";
import { Builders } from "./components/Builders";
import { Strategies } from "./components/Strategies";
import {
  hasPageAccess,
  getDefaultPage,
  getAllowedPages,
} from "./constants/permissions";

interface IRoute {
  path: string;
  component?: JSX.Element;
  key: string;
}

const unauthenticatedRoutes: IRoute[] = [
  {
    path: "/*",
    component: <Login />,
    key: "login",
  },
];

function App() {
  const { tokenId, isAuthenticating, userEmail } = useContext(AuthContext);
  const { isMobileScreen } = useScreenSize();

  const defaultPage = useMemo(() => getDefaultPage(userEmail), [userEmail]);
  const allowedPages = useMemo(() => getAllowedPages(userEmail), [userEmail]);

  // Component that redirects restricted users from dashboard to their first allowed page
  const DashboardWrapper = useMemo(() => {
    if (allowedPages !== null && allowedPages.length > 0) {
      // User is restricted, redirect to their first allowed page
      return function () {
        return <Navigate to={defaultPage} replace />;
      };
    }
    // User has full access, show dashboard
    return function () {
      return !isMobileScreen ? <Dashboard /> : <MobileDashboard />;
    };
  }, [allowedPages, defaultPage, isMobileScreen]);

  const authenticatedRoutes: IRoute[] = useMemo(() => {
    const allRoutes: IRoute[] = [
      ...(makerEnvironment()
        ? [
            {
              path: PageEndpointEnum.MAKERS,
              component: <Makers />,
              key: "makers",
            },
          ]
        : []),
      ...(isAffiliateEnvironment()
        ? [
            {
              path: PageEndpointEnum.AFFILIATES,
              component: <AffiliateAdminPage />,
              key: "affiliates",
            },
          ]
        : []),
      {
        path: "/",
        component: <DashboardWrapper />,
        key: "dashboard",
      },
      {
        path: `${PageEndpointEnum.ACCOUNT}/:account`,
        component: !isMobileScreen ? <Account /> : <MobileAccount />,
        key: "account",
      },
      {
        path: `${PageEndpointEnum.ACCOUNT}/:account/:tab`,
        component: !isMobileScreen ? <Account /> : <MobileAccount />,
        key: "account",
      },
      {
        path: PageEndpointEnum.BOOK,
        component: !isMobileScreen ? <Book /> : undefined,
        key: "book",
      },
      {
        path: PageEndpointEnum.WITHDRAW,
        component: !isMobileScreen ? <Withdraw /> : <Withdraw isMobile />,
        key: "withdraw",
      },
      {
        path: PageEndpointEnum.SETTINGS,
        component: !isMobileScreen ? <Settings /> : undefined,
        key: "settings",
      },
      {
        path: PageEndpointEnum.GEOLOCATIONS,
        component: <Geolocations />,
        key: "geolocations",
      },
      {
        path: PageEndpointEnum.JSON,
        component: !isMobileScreen ? <JSONViewer /> : undefined,
        key: "json",
      },
      {
        path: PageEndpointEnum.TX,
        component: !isMobileScreen ? <TxViewer /> : <MobileTxViewer />,
        key: "tx",
      },
      {
        path: PageEndpointEnum.RISK,
        component: !isMobileScreen ? <RiskPage /> : undefined,
        key: "risk",
      },
      {
        path: PageEndpointEnum.ISOLATED_RISK_MONITOR,
        component: <IsolatedRiskMonitor isMobile={isMobileScreen} />,
        key: "isolated-risk-monitor",
      },
      {
        path: PageEndpointEnum.MMTRADES,
        component: !isMobileScreen ? <MMTrades /> : undefined,
        key: "mm-trades",
      },
      {
        path: PageEndpointEnum.OPTIONSTRADES,
        component: !isMobileScreen ? <OptionsTrades /> : undefined,
        key: "options-trades",
      },
      {
        path: PageEndpointEnum.LIVE_TRADES,
        component: !isMobileScreen ? <LiveTrades /> : undefined,
        key: "live-trades",
      },
      {
        path: PageEndpointEnum.MMSTRATEGIES,
        component: !isMobileScreen ? <MMStrategies /> : undefined,
        key: "mm-strategies",
      },
      {
        path: PageEndpointEnum.DEBUG,
        component: !isMobileScreen ? <Debug /> : undefined,
        key: "debug",
      },
      {
        path: PageEndpointEnum.VOLUME,
        component: <Volume />,
        key: "volume",
      },
      {
        path: PageEndpointEnum.INTERCOM,
        component: <Intercom />,
        key: "intercom",
      },
      {
        path: PageEndpointEnum.OTC,
        component: !isMobileScreen ? <Otc /> : undefined,
        key: "otc",
      },
      {
        path: PageEndpointEnum.SECURITY,
        component: <Security />,
        key: "security",
      },
      {
        path: PageEndpointEnum.ONDO,
        component: <Ondo />,
        key: "ondo",
      },
      {
        path: PageEndpointEnum.BUILDERS,
        component: <Builders />,
        key: "builders",
      },
      {
        path: PageEndpointEnum.REWARDS,
        component: <Stake />,
        key: "rewards",
      },
      {
        path: PageEndpointEnum.STAKE,
        component: <LegacyStakeRedirect />,
        key: "legacy-stake",
      },
      {
        path: PageEndpointEnum.MM_MONITORING,
        component: <MMMonitoring />,
        key: "mm-monitoring",
      },
      {
        path: PageEndpointEnum.MM_CONTROL_PANEL,
        component: <MMControlPanel />,
        key: "mm-control-panel",
      },
      {
        path: PageEndpointEnum.TRADING_PARAMETERS,
        component: !isMobileScreen ? <TradingParameters /> : undefined,
        key: "trading-parameters",
      },
      {
        path: PageEndpointEnum.EQUITY,
        component: !isMobileScreen ? <Equity /> : undefined,
        key: "equity",
      },
      {
        path: PageEndpointEnum.PNL,
        component: !isMobileScreen ? <PnL /> : undefined,
        key: "pnl",
      },
      {
        path: PageEndpointEnum.PERP_DATA,
        component: !isMobileScreen ? <PerpData /> : undefined,
        key: "perp-data",
      },
      {
        path: PageEndpointEnum.STRATEGIES,
        component: !isMobileScreen ? <Strategies /> : undefined,
        key: "strategies",
      },
    ];

    // Filter routes based on permissions
    return allRoutes.filter((route) => {
      // Dashboard should always be accessible
      if (route.path === PageEndpointEnum.DASHBOARD) {
        return true;
      }
      // Account routes with dynamic paths need special handling
      if (route.path.includes(":account") || route.path.includes(":tab")) {
        return true; // Allow account routes for now (they can be restricted later if needed)
      }
      return hasPageAccess(
        userEmail,
        route.path === PageEndpointEnum.STAKE
          ? PageEndpointEnum.REWARDS
          : (route.path as PageEndpointEnum)
      );
    });
  }, [isMobileScreen, userEmail]);

  const routes = useMemo(
    () => (tokenId ? authenticatedRoutes : unauthenticatedRoutes),
    [authenticatedRoutes, tokenId]
  );

  const headerContent = useMemo(() => {
    if (tokenId) {
      if (!isMobileScreen) {
        return <Header />;
      }

      return <MobileHeader />;
    }
  }, [isMobileScreen, tokenId]);

  if (isAuthenticating) {
    return (
      <SpinnerContainerWrapper height={"100vh"}>
        <Spinner />
      </SpinnerContainerWrapper>
    );
  }

  return (
    <GoogleOAuthProvider clientId={CLIENT_ID()}>
      <BrowserRouter>
        <div
          style={{
            height: "100vh",
            overflow: "hidden",
          }}
        >
          {headerContent}
          <Toast />
          <DataContextProvider>
            <ConfigProvider
              theme={{
                algorithm: theme.darkAlgorithm,
                token: {
                  fontSize: 12,
                  colorPrimary: COLORS.blue.one,
                  colorFillSecondary: COLORS.blue.two,
                  fontFamily: "BaseFont",
                  colorPrimaryBg: LAYER_COLORS.one,
                  colorBgContainer: LAYER_COLORS.one,
                  colorBorder: BORDER_COLORS.two,
                  colorBgBase: LAYER_COLORS.one,
                },
                components: {
                  Collapse: {
                    headerBg: LAYER_COLORS.one,
                    contentBg: BACKGROUND_COLORS.five,
                    borderRadius: 6,
                    colorBorder: BORDER_COLORS.one,
                    contentPadding: 0,
                  },
                  Radio: {
                    colorBorder: BORDER_COLORS.two,
                    // colorPrimaryBorder: "red",
                    // colorBorderBg: "red",
                    // colorPrimaryActive: "red",
                    // buttonSolidCheckedColor: "red",
                    colorText: TEXT_COLORS.two,
                  },
                  Tag: {
                    colorSuccessBg: COLORS.green.five,
                    colorSuccess: COLORS.green.one,
                    colorSuccessBorder: COLORS.green.one,
                    colorErrorBg: COLORS.red.five,
                    colorError: COLORS.red.one,
                    colorErrorBorder: COLORS.red.one,
                  },
                },
              }}
            >
              <Routes>
                {routes.map((route) => (
                  <Route
                    key={route.key}
                    path={route.path}
                    element={route.component}
                  />
                ))}
                {/* Redirect unauthorized routes to default page */}
                {tokenId && (
                  <Route
                    path="*"
                    element={<Navigate to={defaultPage} replace />}
                  />
                )}
              </Routes>
            </ConfigProvider>
          </DataContextProvider>
        </div>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

function RootApp() {
  return (
    <>
      <GlobalStyle />
      <ToastContextProvider>
        <AuthContextProvider>
          <App />
        </AuthContextProvider>
      </ToastContextProvider>
    </>
  );
}

export default RootApp;
